import { ServiceContainerDto } from "@apiModels/ServiceContainerDto.csharp";
import { TreatmentsContainerDto } from "@apiModels/TreatmentsContainerDto.csharp";
import React, { FC } from "react";
import { getTreatmentDepth } from "../Helpers/treatmentsHelpers";
import { StepName } from "../Hooks/form";
import { assertNever } from "../Helpers";

type ProgressBarProps = {
	currentStep: number;
	currentStepName: StepName;
    totalSteps: number;
	treatments: (TreatmentsContainerDto | ServiceContainerDto)[];
	currentTreatment: TreatmentsContainerDto | ServiceContainerDto;
	showLabel?: boolean;
};

export const ProgressBar: FC<ProgressBarProps> = (props) => {
	const {
		currentStep,
		currentStepName,
		totalSteps,
		showLabel,
		treatments,
		currentTreatment,
	} = props;

	const treatmentDepth = getTreatmentDepth(treatments, currentTreatment);
    const totalStepsWithoutTreatment = totalSteps - 1;
    const treatmentDepthWithoutService = treatmentDepth - 1;

	const getStepNumbers = (): { active: number; total: number } => {
		switch (currentStepName) {
			case "InitialStep":
				return { active: 1, total: totalStepsWithoutTreatment };
			case "TreatmentStep":
				return {
					active: treatmentDepth + 1,
					total: totalStepsWithoutTreatment + treatmentDepth,
				};
			case "AppointmentStep":
			case "PersonalInfoFormStep":
			case "BookingConfirmedStep":
				return treatmentDepth === 0
					? { active: currentStep, total: totalStepsWithoutTreatment }
					: {
							active: currentStep + treatmentDepthWithoutService,
							total: totalStepsWithoutTreatment + treatmentDepthWithoutService,
					  };
			default:
				return assertNever(currentStepName);
		}
	};

	const { active, total } = getStepNumbers();
	const percentage = (active / total) * 100;
	const progressBarLabel = `Step ${active} of ${total}`;

	const ariaProps = {
		"aria-valuenow": active,
		"aria-valuemin": 1,
		"aria-valuemax": total,
		"aria-label": progressBarLabel,
	};

	return (
		<div className="ProgressBar">
			<div className="ProgressBar__bar">
				<div
					className="ProgressBar__progress"
					role="progressbar"
					style={{ width: `${percentage}%` }}
					{...ariaProps}
				/>
			</div>
			{showLabel && (
				<span className="ProgressBar__label">{progressBarLabel}</span>
			)}
		</div>
	);
};
